import {
  SHImage,
  SHStack,
  SHTableCell,
  SHTableRow,
  SHTypography,
} from "@components/design-systems";
import { AnalysisCommentDTO } from "@models/platform-analysis/entities/comment";
import {
  AnalysisSummaryDTO,
  ProductRecommendationDTO,
} from "@models/platform-analysis/entities/steps/summary";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { parseCommentProductLogo } from "@pages/platform-analysis/_id/steps/summary/components/tables/comments/util";
import { toSentence } from "@utils";
import { memo } from "react";
import { FieldPath } from "react-hook-form";
import { SummaryCommentHeaderCell } from "../header-cell";
import { SummaryCommentCell } from "../row-cell";
import { getCommentFieldPath, getCommentSettingFieldPath } from "../utils";
import { getProductRecommendationConfigs } from "../../../utils";

export const SummaryCommentRow = memo(
  ({
    analysisId,
    rowPath,
    commentSettingFieldPath,
    rowData: analysisComment,
    steps,
    columnWidth,
    isDisabled = false,
    isViewMode = false,
    isAppendix = false,
    recommendationData,
  }: {
    analysisId: string;
    rowPath: FieldPath<AnalysisSummaryDTO>;
    commentSettingFieldPath: FieldPath<AnalysisSummaryDTO>;
    rowData: AnalysisCommentDTO;
    steps: AnalysisStep[];
    columnWidth: number;
    isDisabled?: boolean;
    isViewMode?: boolean;
    isAppendix?: boolean;
    recommendationData?: Partial<ProductRecommendationDTO>;
  }) => {
    const renderRecommendationLabel = (): JSX.Element => {
      if (isAppendix) return <></>;
      const { backgroundColor, icon, label, isCustomType } =
        getProductRecommendationConfigs(recommendationData);

      return (
        <SHStack
          bgcolor={backgroundColor || "transparent"}
          width={"100%"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          {label && (
            <SHStack
              direction={"row"}
              alignItems={"center"}
              gap={0.5}
              justifyContent={"center"}
            >
              {icon}
              <SHTypography
                variant="body3"
                colorVariant="white"
                fontSize="12px"
                sx={{ paddingTop: 0.5 }}
              >
                {isCustomType ? label : toSentence(label)}
              </SHTypography>
            </SHStack>
          )}
        </SHStack>
      );
    };

    return (
      <SHTableRow key={analysisComment.productId} sx={{ borderBottomWidth: 1 }}>
        <SHTableCell sx={{ padding: "0 !important" }}>
          <SHStack margin={"10px 16px 10px 16px"}>
            <SHStack
              direction={"column"}
              spacing={"10px"}
              alignItems={"center"}
            >
              <SHImage
                src={parseCommentProductLogo(analysisComment?.productAvatar)}
                width={"33px"}
                height={"33px"}
              />
              <SHTypography variant="body1" textAlign={"center"}>
                {analysisComment.productName}
              </SHTypography>
            </SHStack>
          </SHStack>

          {renderRecommendationLabel()}
        </SHTableCell>
        <SummaryCommentHeaderCell>{`${analysisComment.featuresMatch} %`}</SummaryCommentHeaderCell>
        {steps.map((step) => {
          return (
            <SummaryCommentCell
              key={`${rowPath}.${step}`}
              fieldPath={getCommentFieldPath(rowPath, step)}
              commentSettingFieldPath={getCommentSettingFieldPath(
                commentSettingFieldPath,
                step,
              )}
              width={`${columnWidth}%`}
              analysisId={analysisId}
              productId={analysisComment.productId}
              step={step}
              isDisabled={isDisabled}
              isViewMode={isViewMode}
              isAppendix={isAppendix}
              removedAtStep={analysisComment.removedFromShortlistAtStep}
            />
          );
        })}
      </SHTableRow>
    );
  },
);
